import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { Form, Col, Row } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import If from '../../components/If';
import { CadernoLPMAAssinatura, CadernoLPMAMonitoramento } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface MonitoramentoModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAMonitoramento) => void;
	dadosMonitoramento?: CadernoLPMAMonitoramento | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: CadernoLPMAMonitoramento = {
	talhaoMonitoramento: '',
	experimentoMonitoramento: '',
	dataInicioMonitoramento: null,
	dataTerminoMonitoramento: null,
	responsavelMonitoramento: '',
	placaBiosseguranca: false,
	areaDelimitada: false,
	estadoCercas: false,
	isolamentoEspacial: false,
	inspecaoRotina: false,
	estadoCercasAcesso: false,
	destruicaoVoluntaria: false,
	montagemIrrigacao: false,
	irrigacaoMonitoramento: false,
	laminaAplicada: '',
	registroPrecipitacao: false,
	dataInicioPrecipitacao: null,
	dataTerminoPrecipitacao: null,
	milimetrosPrecipitacao: 0,
	plantioOutraCultura: false,
	observacoesPlantioOutraCultura: '',
	numeroAMI: '',
	estimativaQuantidade: 0,
	tipoUnidadeEstimativaQuantidade: 'kg',
	unidadeMedidaEstimativaQuantidade: '' as any,
	estadioFenologico: '',
	metodoDestruicaoQuimico: false,
	metodoDestruicaoQuimicoHerbicida: false,
	metodoDestruicaoMecanico: false,
	metodoDestruicaoMecanicoTipo: '' as any,
	metodoDestruicaoOutros: false,
	observacoesMetodoDestruicaoOutros: '',
	dataInicioDestruicao: null,
	dataFinalDestruicao: null,
	observacoesMonitoramento: '',
	checagemFinalMonitoramento: false,
	limpezaDestruicaoVoluntariasJson: undefined,
};

type AbaMonitoramentoId =
	| 'registro'
	| 'verificacoes'
	| 'atividades'
	| 'destruicao'
	| 'limpeza'
	| 'observacoes';

const ABAS_MONITORAMENTO: Array<{ id: AbaMonitoramentoId; label: string }> = [
	{ id: 'registro', label: 'Registro' },
	{ id: 'verificacoes', label: 'Verificações' },
	{ id: 'atividades', label: 'Atividades' },
	{ id: 'destruicao', label: 'Destruição voluntárias' },
	{ id: 'limpeza', label: 'Limpeza' },
	{ id: 'observacoes', label: 'Observações' },
];

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

const normalizarNumero = (valor: any) => {
	if (valor === null || valor === undefined || valor === '') return 0;
	const number = Number(valor);
	return Number.isNaN(number) ? 0 : number;
};

const urlObterAmiPorNumeroAutorizacaoCibio = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?NumeroAutorizacaoCibio=${encodeURIComponent(texto)}`;

const urlObterAmiPorCodigo = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?Codigo=${encodeURIComponent(texto)}`;

const candidatosCodigoAmi = (raw: string): string[] => {
	const valor = raw.trim();
	if (!valor) return [];
	const candidatos: string[] = [];
	const adicionar = (item: string) => {
		const candidato = item.trim();
		if (candidato && !candidatos.includes(candidato)) candidatos.push(candidato);
	};
	adicionar(valor);
	adicionar(valor.replace(/\//g, '.'));
	adicionar(valor.replace(/\./g, '/'));
	if (!/^AMI\s/i.test(valor)) {
		adicionar(`AMI ${valor}`);
		adicionar(`AMI ${valor.replace(/\//g, '.')}`);
	}
	return candidatos;
};

const formatarAmiComBarra = (valor: string): string => {
	const digitos = (valor || '').replace(/\D/g, '').slice(0, 7);
	if (digitos.length <= 3) return digitos;
	return `${digitos.slice(0, 3)}/${digitos.slice(3)}`;
};

export default function MonitoramentoModal({
	show,
	onHide,
	onSubmit,
	dadosMonitoramento,
	assinaturasDisponiveis = [],
}: MonitoramentoModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		watch,
	} = useForm<CadernoLPMAMonitoramento>({
		mode: 'all',
		defaultValues,
		shouldUnregister: false,
	});

	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = useState<number>(1);
	const [abaMonitoramento, setAbaMonitoramento] = useState<AbaMonitoramentoId>('registro');
	const [loadingAmiLink, setLoadingAmiLink] = useState(false);
	const valorNumeroAmi = watch('numeroAMI');

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';
					
					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			
			// Carregar assinatura salva se existir
			if (dadosMonitoramento?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosMonitoramento.assinaturaEnvolvidosBase64);
			} else {
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosMonitoramento?.nomesEnvolvidosAdicionaisJson));
		} else {
			// Limpar estados ao fechar
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosMonitoramento?.assinaturaEnvolvidosBase64, dadosMonitoramento?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQuantidadeLinhasLimpeza(1);
			setAbaMonitoramento('registro');
		} else if (dadosMonitoramento) {
			setAbaMonitoramento('registro');
			// Modo edição - popular formulário com dados existentes
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse do JSON de limpeza
			let limpezas: any[] = [];
			try {
				if (dadosMonitoramento.limpezaDestruicaoVoluntariasJson) {
					limpezas = JSON.parse(dadosMonitoramento.limpezaDestruicaoVoluntariasJson);
				}
			} catch (e) {}

			// Atualiza quantidade de linhas
			setQuantidadeLinhasLimpeza(Math.max(1, limpezas.length));

			// Prepara dados para resetar o formulário
			const dadosParaReset: any = {
				...dadosMonitoramento,
				tipoUnidadeEstimativaQuantidade: dadosMonitoramento.tipoUnidadeEstimativaQuantidade || 'kg',
				dataInicioMonitoramento: parseDate(dadosMonitoramento.dataInicioMonitoramento),
				dataTerminoMonitoramento: parseDate(dadosMonitoramento.dataTerminoMonitoramento),
				dataInicioPrecipitacao: parseDate(dadosMonitoramento.dataInicioPrecipitacao),
				dataTerminoPrecipitacao: parseDate(dadosMonitoramento.dataTerminoPrecipitacao),
				dataInicioDestruicao: parseDate(
					dadosMonitoramento.dataInicioDestruicao ?? dadosMonitoramento.dataDestruicao,
				),
				dataFinalDestruicao: parseDate(dadosMonitoramento.dataFinalDestruicao),
				metodoDestruicaoQuimico:
					!!dadosMonitoramento.metodoDestruicaoQuimico || !!dadosMonitoramento.metodoDestruicaoQuimicoHerbicida,
			};

			// Popular campos de limpeza
			limpezas.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`limpezaNome${index}`] = item.nome || '';
				dadosParaReset[`limpezaDataEntrada${index}`] = parseDate(item.dataEntradaAntes);
				dadosParaReset[`limpezaDataSaida${index}`] = parseDate(item.dataSaidaAntes);
				dadosParaReset[`limpezaMetodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`limpezaMetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`limpezaResponsavel${index}`] = item.responsavel || '';
			});

			reset(dadosParaReset);
		}
	}, [show, dadosMonitoramento, reset]);

	const adicionarLinhaLimpeza = () => setQuantidadeLinhasLimpeza((prev) => prev + 1);

	const removerLinhaLimpeza = (index: number) => {
		if (quantidadeLinhasLimpeza === 1) return;
		setQuantidadeLinhasLimpeza((prev) => prev - 1);
		setValue(`limpezaNome${index}` as any, undefined);
		setValue(`limpezaDataEntrada${index}` as any, null);
		setValue(`limpezaDataSaida${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, undefined);
		setValue(`limpezaMetodoOutros${index}` as any, undefined);
		setValue(`limpezaResponsavel${index}` as any, undefined);
	};

	const renderizarData = (nome: keyof CadernoLPMAMonitoramento) => (
		<Controller
			control={control}
			name={nome as any}
			render={({ onChange, value }) => (
				<DatePicker
					selected={value || null}
					onChange={onChange}
					className="input-custom-to-date"
					locale="pt-BR"
					dateFormat="dd/MM/yyyy"
					placeholderText="Selecionar a data"
				/>
			)}
		/>
	);

	const tentarObterAmiPorIdNumerico = async (valorBruto: string): Promise<number | null> => {
		const valor = valorBruto.trim();
		if (!/^\d+$/.test(valor)) return null;
		const id = parseInt(valor, 10);
		if (!Number.isFinite(id) || id <= 0) return null;
		try {
			const { data, status } = await api.get<{ id?: number }>(
				`api/v1/autorizacoes-cibio/MovimentacaoInterna/${id}`,
			);
			if (status === 200 && typeof data?.id === 'number') return data.id;
		} catch {
		}
		return null;
	};

	const abrirAmiDigitada = async (valorBruto: string) => {
		const valor = (valorBruto || '').trim();
		if (!valor) return;
		setLoadingAmiLink(true);
		try {
			let id = await tentarObterAmiPorIdNumerico(valor);
			if (!id) {
				try {
					const { data } = await api.get(urlObterAmiPorNumeroAutorizacaoCibio(valor));
					id = Array.isArray(data) ? data[0]?.id : data?.id;
				} catch {
				}
			}
			if (!id) {
				for (const candidato of candidatosCodigoAmi(valor)) {
					try {
						const { data } = await api.get(urlObterAmiPorCodigo(candidato));
						id = Array.isArray(data) ? data[0]?.id : data?.id;
						if (id) break;
					} catch {
					}
				}
			}
			if (id) {
				window.open(`/autorizacoes/visualizar/ami?id=${id}`, '_blank');
			} else {
				alert(`AMI ${valor} não encontrada no sistema.`);
			}
		} finally {
			setLoadingAmiLink(false);
		}
	};

	const onSubmitInterno = (dadosForm: CadernoLPMAMonitoramento) => {
		const valores = getValues() as Record<string, any>;
		const dados = { ...dadosForm } as Record<string, any>;

		const limpezas: any[] = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataEntrada = valores[`limpezaDataEntrada${i}`];
			const dataSaida = valores[`limpezaDataSaida${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpezaMetodo${i}`],
				valores[`limpezaMetodoOutros${i}`],
			);
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpezas.push({
					nome,
					dataEntradaAntes: formatarDataParaJson(dataEntrada),
					dataSaidaAntes: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dados[`limpezaNome${i}`];
			delete dados[`limpezaDataEntrada${i}`];
			delete dados[`limpezaDataSaida${i}`];
			delete dados[`limpezaMetodo${i}`];
			delete dados[`limpezaMetodoOutros${i}`];
			delete dados[`limpezaResponsavel${i}`];
		}

		dados.limpezaDestruicaoVoluntariasJson = limpezas.length ? JSON.stringify(limpezas) : null;

		dados.estimativaQuantidade = normalizarNumero(dados.estimativaQuantidade);
		dados.milimetrosPrecipitacao = normalizarNumero(dados.milimetrosPrecipitacao);
		dados.unidadeMedidaEstimativaQuantidade = normalizarNumero(dados.unidadeMedidaEstimativaQuantidade);
		dados.metodoDestruicaoMecanicoTipo = normalizarNumero(dados.metodoDestruicaoMecanicoTipo);
		dados.metodoDestruicaoQuimicoHerbicida = !!dados.metodoDestruicaoQuimico;

		// Adicionar assinatura aos dados
		const dadosComAssinatura = {
			...dados,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};

		onSubmit(dadosComAssinatura as CadernoLPMAMonitoramento);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.MONITORAMENTOS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(onSubmitInterno)} autoComplete="off">
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{ABAS_MONITORAMENTO.map((aba) => (
								<Button
									key={aba.id}
									title={aba.label}
									contained={abaMonitoramento === aba.id}
									className={abaMonitoramento === aba.id ? 'active' : ''}
									type="button"
									onClick={() => setAbaMonitoramento(aba.id)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				{abaMonitoramento === 'registro' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h3>Formulário – Registros de atividades de Monitoramento</h3>
						</Form.Label>
						<p className="mb-0"><strong>Caderno de Conformidade de Campo (FCN) 81</strong></p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos e datas inicial/final de realização das atividades</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Talhão</Form.Label>
							<Form.Control ref={register} name="talhaoMonitoramento" placeholder="Talhão" />
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Experimento</Form.Label>
							<Form.Control ref={register} name="experimentoMonitoramento" placeholder="Experimento" />
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							{renderizarData('dataInicioMonitoramento')}
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							{renderizarData('dataTerminoMonitoramento')}
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<hr />
					</React.Fragment>
				)}

				{abaMonitoramento === 'verificacoes' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de verificações</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="placaBiosseguranca"
							label="Placa de identificação/biossegurança do ensaio presente no local, em bom estado de conservação e com informações legíveis e atualizadas."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="estadoCercas"
							label="Cercas e portões em bom estado de conservação."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="areaDelimitada"
							label="Área experimental delimitada (estacas, bandeiras, cercas)."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="isolamentoEspacial"
							label="Isolamento permanece mantido."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="inspecaoRotina"
							label="Inspeção de rotina para observações de ocorrências anormais na área experimental (presença de animais, erosão etc.)."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="estadoCercasAcesso"
							label="Inexistência de plantas voluntárias e/ou rebrotes em estádio reprodutivo."
						/>
					</Form.Group>
				</Form.Row>

				<hr />
					</React.Fragment>
				)}

				{abaMonitoramento === 'atividades' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de atividades</h5>
							<small>* Registro de irrigação e precipitação somente para monitoramento de 4 meses</small>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Check
							type="checkbox"
							ref={register}
							name="destruicaoVoluntaria"
							label="Destruição de plantas voluntárias (registro no campo de destruição de plantas voluntárias abaixo)."
						/>
						<Form.Check
							type="checkbox"
							ref={register}
							name="montagemIrrigacao"
							label="Montagem da irrigação."
						/>
						<div className="d-flex align-items-center" style={{ gap: '16px', flexWrap: 'wrap' }}>
							<Form.Check
								type="checkbox"
								ref={register}
								name="irrigacaoMonitoramento"
								label="Irrigação durante o monitoramento."
							/>
							<Form.Control
								ref={register}
								name="laminaAplicada"
								placeholder="Lâmina aplicada (mm)"
								style={{ maxWidth: 220 }}
							/>
						</div>
						<Form.Check
							type="checkbox"
							ref={register}
							name="registroPrecipitacao"
							label="Registro de precipitação."
						/>
						<Row className="mt-2">
							<Col md={3}>
								<Form.Group>
									<Form.Label>Período início</Form.Label>
									{renderizarData('dataInicioPrecipitacao')}
								</Form.Group>
							</Col>
							<Col md={3}>
								<Form.Group>
									<Form.Label>Período término</Form.Label>
									{renderizarData('dataTerminoPrecipitacao')}
								</Form.Group>
							</Col>
							<Col md={3}>
								<Form.Group>
									<Form.Label>mm acumulado</Form.Label>
									<Form.Control ref={register} name="milimetrosPrecipitacao" type="number" step="0.01" />
								</Form.Group>
							</Col>
						</Row>
						<Form.Check
							type="checkbox"
							ref={register}
							name="plantioOutraCultura"
							label="Plantio de outra cultura (registro no formulário específico neste caderno de campo)."
						/>
						<Form.Control
							className="mt-2"
							ref={register}
							name="observacoesPlantioOutraCultura"
							placeholder="Observações e referência da página"
						/>
						<Form.Check
							className="mt-3"
							type="checkbox"
							ref={register}
							name="checagemFinalMonitoramento"
							label="Checagem final e encerramento do monitoramento (registro nos formulários de colheita, destruição da LPMA)."
						/>
						<p className="mt-3 mb-0">
							<small>Outras atividades – descrever abaixo no campo de observações e informações adicionais.</small>
						</p>
					</Form.Group>
				</Form.Row>

				<hr />
					</React.Fragment>
				)}

				{abaMonitoramento === 'destruicao' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Destruição de plantas voluntárias</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={4}>
						<Form.Label>N.&ordm; da AMI</Form.Label>
						<Controller
							control={control}
							name="numeroAMI"
							render={({ onChange, value }) => (
								<Form.Control
									value={value || ''}
									placeholder="Informe o número da AMI"
									onChange={(e) => onChange(formatarAmiComBarra(e.target.value))}
								/>
							)}
						/>
						{(valorNumeroAmi || '').trim() ? (
							<button
								type="button"
								className="btn btn-link p-0 mt-1"
								disabled={loadingAmiLink}
								onClick={() => void abrirAmiDigitada(valorNumeroAmi || '')}
							>
								{loadingAmiLink
									? 'Buscando AMI...'
									: `Visualizar AMI ${(valorNumeroAmi || '').trim()}`}
							</button>
						) : null}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={4}>
						<Form.Label>Estimativa/quantidade</Form.Label>
						<div className="d-flex" style={{ gap: '8px' }}>
							<Controller
								control={control}
								name="tipoUnidadeEstimativaQuantidade"
								defaultValue="kg"
								render={({ onChange, value }) => (
									<Form.Control
										as="select"
										value={value || 'kg'}
										onChange={(e) => onChange(e.target.value)}
										style={{ maxWidth: 120 }}
									>
										<option value="kg">Kg</option>
										<option value="gramas">Gramas</option>
										<option value="unidades">Unidades</option>
									</Form.Control>
								)}
							/>
							<Form.Control
								ref={register}
								name="estimativaQuantidade"
								type="number"
								step="0.01"
								placeholder="Quantidade"
							/>
						</div>
					</Form.Group>
					<Form.Group as={Col} md={4}>
						<Form.Label>Unidade</Form.Label>
						<Form.Control as="select" ref={register} name="unidadeMedidaEstimativaQuantidade">
							<option value="">Selecione</option>
							<option value="1">m²</option>
							<option value="2">ha</option>
							<option value="3">Área da LPMA</option>
						</Form.Control>
					</Form.Group>
					<Form.Group as={Col} md={4}>
						<Form.Label>Estádio fenológico</Form.Label>
						<Form.Control ref={register} name="estadioFenologico" placeholder="Estádio fenológico" />
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Métodos de destruição</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check
								type="checkbox"
								ref={register}
								name="metodoDestruicaoQuimico"
								label="Quimico/Herbicida"
							/>
							<Form.Check
								type="checkbox"
								ref={register}
								name="metodoDestruicaoMecanico"
								label="Mecânico"
							/>
						</div>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Método mecânico aplicado</Form.Label>
						<Form.Control as="select" ref={register} name="metodoDestruicaoMecanicoTipo">
							<option value="">Selecione</option>
							<option value="1">Grade</option>
							<option value="2">Enxada rotativa</option>
							<option value="3">Roçadeira</option>
							<option value="4">Triton</option>
							<option value="5">Arranquio / capina manual</option>
						</Form.Control>
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Check
							className="mb-2"
							type="checkbox"
							ref={register}
							name="metodoDestruicaoOutros"
							label="Outros métodos"
						/>
						<Form.Control
							className="mt-2"
							ref={register}
							name="observacoesMetodoDestruicaoOutros"
							placeholder="Especificar outros métodos"
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={4}>
						<Form.Label>Data de início da destruição</Form.Label>
						{renderizarData('dataInicioDestruicao')}
					</Form.Group>
					<Form.Group as={Col} md={4}>
						<Form.Label>Data final da destruição</Form.Label>
						{renderizarData('dataFinalDestruicao')}
					</Form.Group>
				</Form.Row>

				<hr />
					</React.Fragment>
				)}

				{abaMonitoramento === 'limpeza' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza de maquinários/equipamentos utilizados na destruição das voluntárias</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom font-weight-bold text-uppercase small text-muted">
					<Col md={4}>Nome / Código</Col>
					<Col md={2}>Antes ENTRADA</Col>
					<Col md={2}>Antes SAÍDA</Col>
					<Col md={2}>Método</Col>
					<Col md={2}>Responsável</Col>
				</Form.Row>

				{Array.from({ length: quantidadeLinhasLimpeza }).map((_, index) => {
					const metodoSelecionado = watch(`limpezaMetodo${index}` as any);

					return (
						<Form.Row className="row-custom align-items-end" key={`limpeza-voluntaria-${index}`}>
							<Form.Group as={Col} md={4}>
								<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário / equipamento" />
							</Form.Group>
							<Form.Group as={Col} md={2}>
								<Controller
									control={control}
									name={`limpezaDataEntrada${index}` as any}
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={2}>
								<Controller
									control={control}
									name={`limpezaDataSaida${index}` as any}
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Form.Group>
							<Form.Group as={Col} md={2}>
								<MetodoLimpezaSelect
									register={register}
									name={`limpezaMetodo${index}`}
									nameOutros={`limpezaMetodoOutros${index}`}
									metodoSelecionado={metodoSelecionado}
								/>
							</Form.Group>
							<Form.Group as={Col} md={2}>
								<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" />
							</Form.Group>
							<Col md="auto" className="d-flex justify-content-end mb-3 mb-md-0">
								{quantidadeLinhasLimpeza > 1 && (
									<Button
										title="Remover"
										contained={false}
										type="button"
										onClick={() => removerLinhaLimpeza(index)}
									/>
								)}
							</Col>
						</Form.Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>

				<hr />
					</React.Fragment>
				)}

				{abaMonitoramento === 'observacoes' && (
					<React.Fragment>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesMonitoramento"
							placeholder="Registre detalhamentos adicionais das atividades realizadas"
						/>
					</Form.Group>
				</Form.Row>
					</React.Fragment>
				)}

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

